import {
  AuditItemsFilters,
  type AuditItemFilters,
  type AuditItemPagination,
  type AuditItemFilterOptions,
  getPaginationInfo,
} from '@internal-audit/components/AuditItemsFilters';
import type { GraphQLAuditItem, FlexibleAuditSession } from '@internal-audit/domain/graphql-types';
import { useGetAuditSessionStats } from '@internal-audit/hooks/useGetAuditSessionStats';
import { Section } from '@soteria/design-system-ve';
import { useCallback, useMemo } from 'react';

import { AuditItemSectionHeader } from './AuditItemSectionHeader';
import { AuditItemShimmer } from './AuditItemShimmer';
import { AuditItemsList } from './AuditItemsList';
import { AuditItemsPagination } from './AuditItemsPagination';
import { DataListShimmer } from './DataListShimmer';

interface AuditItemSectionProps {
  items: GraphQLAuditItem[];
  session?: FlexibleAuditSession | null; // Add session data for progress
  isLoading?: boolean; // Full page loading (initial load)
  isDataLoading?: boolean; // Data list loading (filters/pagination)
  totalItemsCount?: number; // Total items before filtering
  // Filter props
  filters: AuditItemFilters;
  onFiltersChange: (filters: AuditItemFilters) => void;
  // Pagination props
  pagination: AuditItemPagination;
  onPaginationChange: (pagination: AuditItemPagination) => void;
  filterOptions: AuditItemFilterOptions;
  // Raw items with decisions data from the hook
  rawItemsWithDecisions?: Array<{
    id: string;
    decisions: any[]; // eslint-disable-line @typescript-eslint/no-explicit-any
  }>;
  // Item selection for side panel
  onItemSelect?: (item: GraphQLAuditItem) => void;
  // Currently selected item ID
  selectedItemId?: string;
  // Panel loading state for the selected item
  panelLoading?: boolean;
  // Whether the audit session is completed (disables interactions)
  isSessionCompleted?: boolean;
  // Open conversation context modal for an item
  onContext?: (item: GraphQLAuditItem) => void;
}

// Helper function to calculate session progress percentage
function calculateSessionProgressPercentage(
  stats: { totalCount: number; completeCount: number; skippedCount: number; incompleteCount: number } | null | undefined,
  utils: { getCompletionPercentage: () => number } | null | undefined,
  session: FlexibleAuditSession | null | undefined
): number {
  if (stats && utils) {
    return utils.getCompletionPercentage();
  }

  // Fallback to session data calculation
  if (!session || !session.itemsCount || session.itemsCount === 0) return 0;

  const completedItems = session.completedCount || 0;
  const skippedItems = session.skippedCount || 0;

  return Math.round(((completedItems + skippedItems) / session.itemsCount) * 100);
}

// eslint-disable-next-line complexity
export function AuditItemSection({
  items,
  session,
  isLoading = false,
  isDataLoading = false,
  totalItemsCount,
  filters,
  onFiltersChange,
  pagination,
  onPaginationChange,
  filterOptions,
  rawItemsWithDecisions = [],
  onItemSelect,
  selectedItemId,
  panelLoading = false,
  isSessionCompleted = false,
  onContext,
}: AuditItemSectionProps) {
  // Use the audit stats hook for accurate server-side calculations
  const { stats, utils } = useGetAuditSessionStats(session?.id, {
    skip: !session?.id,
    pollInterval: 30000, // Poll every 30 seconds for live updates
  });

  // Create a map of item ID to decisions for easy lookup
  const decisionsMap = useMemo(() => {
    const map = new Map();

    rawItemsWithDecisions?.forEach(rawItem => {
      map.set(rawItem.id, rawItem.decisions || []);
    });

    return map;
  }, [rawItemsWithDecisions]);

  // Handle decision submit from Like button
  const handleDecisionSubmit = useCallback((_itemId: string, _hasDecision: boolean) => {
    // This is handled optimistically by the useUpsertAuditDecision hook
    // No additional local state management needed since the hook handles cache updates
  }, []);

  // Handle skip toggle from buttons
  const handleSkipToggle = useCallback((_itemId: string, _isSkipped: boolean) => {
    // This is handled optimistically by the useSkipAuditItem hook
    // No additional local state management needed since the hook handles cache updates
  }, []);

  // Handle decision select/update - no longer needed since parent handles data
  const handleDecisionSelect = useCallback((_decisionId: string) => {
    // Data refresh is now handled by the mutation's refetchQueries
    // No additional action needed here
  }, []);

  // Use audit stats hook for accurate progress calculation, fallback to session data
  const sessionProgressPercentage = useMemo(() => calculateSessionProgressPercentage(stats, utils, session), [stats, utils, session]);

  // Calculate pagination info for display
  const paginationInfo = useMemo(() => {
    if (!totalItemsCount) return null;

    return getPaginationInfo(totalItemsCount, pagination);
  }, [totalItemsCount, pagination]);

  // Handle pagination change
  const handlePageChange = useCallback(
    (page: number) => {
      onPaginationChange({
        ...pagination,
        currentPage: page,
      });
    },
    [pagination, onPaginationChange]
  );

  // Determine if filters are applied
  const isFiltered = totalItemsCount !== undefined && totalItemsCount !== items.length;
  const filteredCount = items.length;

  // Render full loading state with shimmer (initial page load)
  if (isLoading) {
    return <AuditItemShimmer />;
  }

  return (
    <Section>
      {/* Always show the header with progress bar */}
      <AuditItemSectionHeader session={session} sessionProgressPercentage={sessionProgressPercentage} stats={stats} />

      {/* Audit Items Filters - positioned after header, never show loading state so filters stay interactive */}
      <AuditItemsFilters
        filterOptions={filterOptions}
        filteredCount={filteredCount}
        filters={filters}
        isLoading={false} // Never pass loading state to filters - keep them interactive
        pagination={pagination}
        totalCount={totalItemsCount}
        onFiltersChange={onFiltersChange}
        onPaginationChange={onPaginationChange}
      />

      {/* Audit Items List with shimmer for data loading or actual items */}
      {isDataLoading ? (
        <DataListShimmer />
      ) : (
        <AuditItemsList
          decisionsMap={decisionsMap}
          isFiltered={isFiltered}
          isSessionCompleted={isSessionCompleted}
          items={items}
          panelLoading={panelLoading}
          selectedItemId={selectedItemId}
          onContext={onContext}
          onDecisionSelect={handleDecisionSelect}
          onDecisionSubmit={handleDecisionSubmit}
          onItemSelect={onItemSelect}
          onSkipToggle={handleSkipToggle}
        />
      )}

      {/* Pagination Controls - don't show loading state to keep them interactive */}
      <AuditItemsPagination isLoading={false} pagination={pagination} paginationInfo={paginationInfo} onPageChange={handlePageChange} />
    </Section>
  );
}
